import styles from './n21-header.module.css';
import N25SearchBar from '../components/n25-search-bar/n25-search-bar.component';
import MenuHamburger from '../components/menu-hamburger/menu-hamburger.component';
import { memo, useCallback, useEffect, useState } from 'react';
import { NavigationNode } from '~/lib/data-contract/auto-generated/api-model';
import MetaList from '../components/meta-list/meta-list.component';
import MegaMenu from '../components/mega-menu/mega-menu.component';
import { useFrame, useTranslation, withErrorBoundary } from '~/shared/utils';
import HeaderWrapper from '../header-wrapper/header-wrapper.component';
import HeaderLogo from '../components/header-logo/header-logo.component';
import BackButton from '../components/back-button/back-button.component';
import Icons from '../components/icons/icons.component';
import { usePage } from '~/templates/pages/hooks/usePage';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import SubMenu from '../components/sub-menu/sub-menu.component';
import N70NavigationIndicator from '../components/n70-navigation-indicator/n70-navigation-indicator.component';

const N21Header = memo(() => {
    const { data } = useFrame();
    const { mainMenu = [], metaMenu = [] } = data?.navigation || {};
    const { type: pageType } = usePage();
    const { translate } = useTranslation();

    const [isOpenMobile, setIsOpenMobile] = useState(false);
    const [selectedSubMenu, setSelectedSubMenu] = useState<NavigationNode | null>(null);

    const router = useRouter();

    const handleMenuToggle = useCallback(() => {
        setIsOpenMobile((prev) => {
            if (prev) {
                setSelectedSubMenu(null);
            }
            return !prev;
        });
    }, []);

    const handleCloseMenu = useCallback(() => {
        setSelectedSubMenu(null);
        setIsOpenMobile(false);
    }, []);

    useEffect(() => {
        router.events.on('routeChangeStart', handleCloseMenu);
        return () => {
            router.events.off('routeChangeStart', handleCloseMenu);
        };
    }, [router.events, handleCloseMenu]);

    const searchUrl = data?.staticLinks?.searchPage?.url || '';
    const isSearchPage = pageType === ('m170searchPage' as unknown);

    return (
        <HeaderWrapper
            className={styles.root}
            innerClassName={styles.innerWrapper}
            isSearchPage={isSearchPage}
        >
            {isSearchPage && (
                <BackButton
                    text={translate('navigation.back')}
                    isSearchPage={isSearchPage}
                    onClick={() => router.back()}
                    selectedSubMenu={!!selectedSubMenu}
                />
            )}

            <div className={clsx(styles.logoWrapper, { [styles.searchPage]: isSearchPage })}>
                <BackButton
                    onClick={() => setSelectedSubMenu(null)}
                    selectedSubMenu={!!selectedSubMenu}
                />
                <HeaderLogo
                    selectedSubMenu={!!selectedSubMenu}
                    mobileInverted
                    isSearchPage={isSearchPage}
                />
            </div>

            <N25SearchBar
                onFocus={handleCloseMenu}
                className={clsx(styles.search, { [styles.searchPage]: isSearchPage })}
                selectedSubMenu={selectedSubMenu}
                searchUrl={searchUrl}
                isSearchPage={isSearchPage}
            />

            <MetaList metaMenu={metaMenu} className={styles.metaList} />
            <MegaMenu
                navigation={mainMenu}
                metaNavigation={metaMenu}
                isOpenMobile={isOpenMobile}
                selectedSubMenu={selectedSubMenu}
                setSelectedSubMenu={setSelectedSubMenu}
                className={styles.megaMenu}
            />
            {mainMenu.map((subMenu) => (
                <SubMenu
                    key={subMenu.id}
                    menuContent={subMenu}
                    isSelected={selectedSubMenu?.id === subMenu.id}
                    onMenuClick={setSelectedSubMenu}
                />
            ))}
            <div className={styles.iconsWrapper}>
                <Icons
                    className={clsx(styles.icons, { [styles.searchPage]: isSearchPage })}
                    isSearchPage={isSearchPage}
                />
                <MenuHamburger
                    onClick={handleMenuToggle}
                    isOpen={isOpenMobile}
                    isSearchPage={isSearchPage}
                />
            </div>
            <N70NavigationIndicator />
        </HeaderWrapper>
    );
});

N21Header.displayName = 'N21Header';
export default withErrorBoundary(N21Header);
